import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, Typography,
  TablePagination, InputAdornment, TextField, Box, Paper,
  IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';

import api from "../api";
import AgregarInfraccion from "./utilities/AgregarInfraccion";
import EditarInfraccion from "./utilities/EditarInfraccion";
import DetalleInfraccion from "./utilities/DetalleInfraccion";

const Infracciones = () => {
  const [infracciones, setInfracciones] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInfraccion, setSelectedInfraccion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuInfraccion, setMenuInfraccion] = useState(null);

  useEffect(() => {
    fetchInfracciones();
  }, []);

  const fetchInfracciones = () => {
    api.get("infracciones/")
      .then((response) => {
        console.log('RAW INFRACCIONES');
        console.log(response.data);
        setInfracciones(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener infracciones:", error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta infracción?")) {
      api.delete(`infracciones/${id}/`)
        .then(() => {
          setInfracciones(infracciones.filter(i => i.id !== id));
        })
        .catch(error => {
          console.error("Error al eliminar la infracción:", error);
        });
    }
  };

  const handleMenuOpen = (event, infraccion) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuInfraccion(infraccion);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuInfraccion(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null);
  };

  const handleRowClick = (infraccion) => {
    setSelectedInfraccion(infraccion);
  };

  const handleDetailClose = () => {
    setSelectedInfraccion(null);
  };

  const normalize = (str) =>
    str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const search = normalize(searchTerm);

  const filteredInfracciones = infracciones.filter((i) => {
    const fechaFormateada = new Date(i.fecha).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const montoTexto = i.monto != null ? i.monto.toString() : "";

    // Add as many relevant keywords as you want here for pagada:
    const pagadaTexto = i.pagada
      ? "pagado pagada"
      : "impaga impago";

    return (
      normalize(i.descripcion || "").includes(search) ||
      normalize(i.vehiculo_nombre || "").includes(search) ||
      normalize(i.conductor || "").includes(search) ||
      normalize(fechaFormateada).includes(search) ||
      normalize(montoTexto).includes(search) ||
      normalize(pagadaTexto).includes(search)
    );
  });

  const handleInfraccionesUpdated = (updatedInfraccion) => {
    setInfracciones((prevInfracciones) => {
      const exists = prevInfracciones.some((i) => i.id === updatedInfraccion.id);
      if (exists) {
        return prevInfracciones.map((i) =>
          i.id === updatedInfraccion.id ? updatedInfraccion : i
        );
      } else {
        return [...prevInfracciones, updatedInfraccion];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("infracciones/"); // Adjust endpoint if needed for all records
      const allData = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Infracciones");

      XLSX.writeFile(workbook, "infracciones.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color='#152d51'>
          Infracciones
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar infracción"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          <Button onClick={() => setOpen(true)} style={{ border: "1px solid #3f51b5", borderRadius: "6px", color: "#3f51b5" }}>
            Agregar Infracción <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Vehículo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Conductor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Monto</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Pagada</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInfracciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((infraccion) => (
              <TableRow key={infraccion.id} sx={{ '&:hover': { backgroundColor: '#f1f1f1', cursor: 'pointer' } }} onClick={() => handleRowClick(infraccion)}>
                <TableCell>{infraccion.vehiculo_nombre}</TableCell>
                <TableCell>{infraccion.conductor}</TableCell>
                <TableCell>
                  {infraccion.fecha
                    ? new Date(infraccion.fecha).toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                    : "—"}
                </TableCell>
                <TableCell>{infraccion.descripcion}</TableCell>
                <TableCell>{infraccion.monto} ARS</TableCell>
                <TableCell>{infraccion.pagada === true ? "Sí" : "No"}</TableCell>
                <TableCell>
                  <IconButton onClick={(event) => handleMenuOpen(event, infraccion)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredInfracciones.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuInfraccion.id); handleMenuClose(); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      <Dialog open={!!selectedInfraccion} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DialogContent>
          <DetalleInfraccion selectedInfraccion={selectedInfraccion} handleDetailClose={handleDetailClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} color="secondary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarInfraccion handleClose={() => setOpen(false)} onUpdate={handleInfraccionesUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <EditarInfraccion infraccion={menuInfraccion} handleClose={() => setOpenEdit(false)} onUpdate={handleInfraccionesUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Infracciones;
